import { motion } from "framer-motion";
import { Megaphone, Search, Palette, Globe, Database, Mail, Bot, Workflow, CalendarCheck, BarChart3, Target, CheckCircle2 } from "lucide-react";

const stages = [
  {
    step: "01",
    title: "Generate Demand",
    subtitle: "Growth Systems",
    description: "We generate high-quality demand using performance-driven channels and content strategies.",
    outcome: "Consistent, high-quality leads",
    services: [
      { icon: Megaphone, name: "Paid Ads (Google Ads, Meta Ads)" },
      { icon: Search, name: "Search Engine Optimization (SEO)" },
      { icon: Palette, name: "Content Strategy & Creative" },
    ],
    gradient: "from-primary/5 to-primary/10",
    accent: "text-primary",
    accentBg: "bg-primary/10",
  },
  {
    step: "02",
    title: "Capture & Nurture",
    subtitle: "Conversion Systems",
    description: "We ensure every lead is captured, organized, and nurtured with the right messaging at the right time.",
    outcome: "No lost leads, better qualification, stronger engagement",
    services: [
      { icon: Globe, name: "Website & Landing Page Development" },
      { icon: Database, name: "CRM Setup & Sales Enablement" },
      { icon: Mail, name: "Email Marketing & Lead Nurturing" },
      { icon: Bot, name: "AI Agents & Chatbots" },
    ],
    gradient: "from-accent/5 to-accent/10",
    accent: "text-primary",
    accentBg: "bg-primary/10",
  },
  {
    step: "03",
    title: "Convert & Optimize",
    subtitle: "Revenue Systems",
    description: "We turn leads into booked customers and continuously optimize performance using automation and data.",
    outcome: "Higher booking rates, better conversion, measurable ROI",
    services: [
      { icon: Workflow, name: "Workflow Automation (SMS, WhatsApp, Email)" },
      { icon: CalendarCheck, name: "Booking Systems & Scheduling Flows" },
      { icon: BarChart3, name: "Analytics & Performance Tracking" },
    ],
    gradient: "from-primary/5 to-accent/10",
    accent: "text-primary",
    accentBg: "bg-primary/10",
  },
];

export const SystemSection = () => {
  return (
    <section className="py-20 px-4 bg-secondary">
      <div className="max-w-5xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
            <Target className="w-4 h-4" />
            <span className="text-sm font-medium">Our System</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            How We Turn Leads Into Customers
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Our system is built in three stages to ensure every lead has the highest chance of becoming a customer.
          </p>
        </motion.div>

        <div className="space-y-8">
          {stages.map((stage, i) => (
            <motion.div
              key={i}
              className={`rounded-2xl border border-border bg-card p-8 md:p-10`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
            >
              <div className="flex flex-col md:flex-row md:items-start gap-6">
                {/* Step number */}
                <div className={`w-14 h-14 rounded-2xl ${stage.accentBg} flex items-center justify-center flex-shrink-0`}>
                  <span className={`text-xl font-bold ${stage.accent}`}>{stage.step}</span>
                </div>

                <div className="flex-1">
                  {/* Header */}
                  <div className="mb-4">
                    <span className={`text-sm font-medium ${stage.accent} uppercase tracking-wide`}>{stage.subtitle}</span>
                    <h3 className="text-2xl font-bold text-foreground mt-1">{stage.title}</h3>
                    <p className="text-muted-foreground mt-2">{stage.description}</p>
                  </div>

                  {/* Services */}
                  <div className="grid sm:grid-cols-2 gap-3 mb-5">
                    {stage.services.map((service, j) => (
                      <div key={j} className="flex items-center gap-3 p-3 rounded-lg bg-muted">
                        <service.icon className="w-5 h-5 text-primary flex-shrink-0" />
                        <span className="text-sm font-medium text-foreground">{service.name}</span>
                      </div>
                    ))}
                  </div>

                  {/* Outcome */}
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-sm font-medium text-foreground">
                      Outcome: <span className="text-muted-foreground">{stage.outcome}</span>
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
